/**
 * Initial state construction, and the typed deep copy the tick loop runs on.
 *
 * The planet comes from seeded world generation (spec §6); starting inventory
 * is the home hex's scavenged material (spec §6 rule 4). The city is empty and
 * the colony lands with four standard drones and two mechs.
 */

import {
  CITY_GRID_SIZE,
  FACTORY_SOLAR_OUTPUT,
  STARTING_MECHS,
  STARTING_DRONES,
} from './constants';
import {
  DRONE_CLASSES_DATA,
  ELEMENT_IDS,
  GOOD_IDS,
  MINERAL_IDS,
  type ElementId,
  type GoodId,
  type MineralId,
} from './data';
import { emptyColony } from './colony';
import { emptyMarket } from './market';
import { generateWorld } from './worldgen';
import type {
  Drone,
  GameState,
  Inventory,
  Plot,
  SynthesisSetting,
} from './types';

function zeroed<K extends string>(ids: readonly K[]): Record<K, number> {
  const out = {} as Record<K, number>;
  for (const id of ids) out[id] = 0;
  return out;
}

export function emptyInventory(): Inventory {
  return {
    minerals: zeroed<MineralId>(MINERAL_IDS),
    elements: zeroed<ElementId>(ELEMENT_IDS),
    goods: zeroed<GoodId>(GOOD_IDS),
  };
}

function emptyGrid(): (Plot | null)[][] {
  const grid: (Plot | null)[][] = [];
  for (let y = 0; y < CITY_GRID_SIZE; y++) {
    const row: (Plot | null)[] = [];
    for (let x = 0; x < CITY_GRID_SIZE; x++) row.push(null);
    grid.push(row);
  }
  return grid;
}

function defaultSynthesis(): Record<GoodId, SynthesisSetting> {
  const out = {} as Record<GoodId, SynthesisSetting>;
  // Priority follows GOOD_IDS order, which follows the spec §4.4 table order.
  // ENABLED, and uncapped. Every recipe used to start switched OFF, so a new
  // game refined ore into elements and then simply stopped: minerals climbing,
  // components at zero, buildings queued behind components that would never be
  // made, and nothing on screen saying why. The reference player in the test
  // harness turned all seven on at tick 0, so the automated runs never saw it
  // and every playtest looked healthy.
  //
  // A factory that has landed and been told to build a colony does not need
  // permission to make steel. The switches remain, for turning things OFF.
  GOOD_IDS.forEach((id, index) => {
    out[id] = { enabled: true, priority: index, target: 0 };
  });
  return out;
}

/**
 * The drones the colony lands with. SPEC-ERRATA E23 — the spec provides three
 * minis; this is four standards, and the mini class no longer exists.
 */
function startingDrones(): Drone[] {
  const standard = DRONE_CLASSES_DATA.standard;
  const drones: Drone[] = [];
  for (let i = 0; i < STARTING_DRONES; i++) {
    drones.push({
      id: i,
      class: 'standard',
      capacity: standard.capacity,
      speedKmPerTick: standard.speedKmPerTick,
      assignedHex: null,
      phase: 'idle',
      phaseDeciTicksRemaining: 0,
      cargo: null,
      // spec §13 item 3: manual by default, so depletion registers as an event.
      autoReassign: false,
    });
  }
  return drones;
}

/**
 * Deep copy of a GameState.
 *
 * Hand-written rather than `structuredClone` because step() copies once per
 * tick, and the generic algorithm — which walks arbitrary types and tracks
 * cycles it will never find here — measured ~110 microseconds on an empty
 * state. That is over a second for a 10,800-tick session before the world
 * contains anything, and it grows with the grid. A typed copy knows the shape
 * and skips all of that.
 *
 * The cost of hand-writing it is that a new field on GameState must be added
 * here too. `cloneState matches a structuredClone reference copy` in the
 * determinism suite fails loudly if one is forgotten.
 */
export function cloneState(state: GameState): GameState {
  const hexes: GameState['planet']['hexes'] = {};
  for (const key of Object.keys(state.planet.hexes)) {
    const hex = state.planet.hexes[key];
    if (hex === undefined) continue;
    hexes[key] = {
      coord: { q: hex.coord.q, r: hex.coord.r },
      ring: hex.ring,
      deposits: { ...hex.deposits },
      initialDeposits: { ...hex.initialDeposits },
      surveyed: hex.surveyed,
      assignedDrones: [...hex.assignedDrones],
    };
  }

  const synthesis = {} as GameState['factory']['synthesis'];
  for (const id of GOOD_IDS) synthesis[id] = { ...state.factory.synthesis[id] };

  const grid: (Plot | null)[][] = state.city.grid.map((row) =>
    row.map((plot) => (plot === null ? null : { ...plot, coord: { ...plot.coord } })),
  );

  return {
    tick: state.tick,
    seed: state.seed,
    rng: { ...state.rng },
    planet: {
      hexes,
      atmosphere: { ...state.planet.atmosphere },
    },
    factory: {
      solarOutput: state.factory.solarOutput,
      synthesis,
      assembly: state.factory.assembly === null ? null : { ...state.factory.assembly },
      assemblyQueue: state.factory.assemblyQueue.map((order) => ({ ...order })),
    },
    drones: state.drones.map((drone) => ({
      ...drone,
      assignedHex: drone.assignedHex === null ? null : { ...drone.assignedHex },
      cargo: drone.cargo === null ? null : { ...drone.cargo },
    })),
    mechs: { ...state.mechs },
    city: { grid },
    inventory: {
      minerals: { ...state.inventory.minerals },
      elements: { ...state.inventory.elements },
      goods: { ...state.inventory.goods },
    },
    buildQueue: state.buildQueue.map((item) => ({ ...item, plot: { ...item.plot } })),
    power: { ...state.power },
    colony: {
      population: state.colony.population,
      food: {
        buckets: [...state.colony.food.buckets],
        bucketAge: state.colony.food.bucketAge,
      },
      happinessBp: state.colony.happinessBp,
      totalArrived: state.colony.totalArrived,
      totalDeparted: state.colony.totalDeparted,
    },
    market: {
      credits: state.market.credits,
      basePrice: { ...state.market.basePrice },
      priceBp: { ...state.market.priceBp },
      orders: Object.fromEntries(
        Object.entries(state.market.orders).map(([k, v]) => [k, { ...v }]),
      ) as GameState['market']['orders'],
      lastTraded: { ...state.market.lastTraded },
    },
    production: {
      goods: { ...state.production.goods },
      ticks: state.production.ticks,
    },
    starved:
      state.starved === null
        ? null
        : {
            reason: state.starved.reason,
            missing: { ...state.starved.missing },
            projectedWaitTicks: state.starved.projectedWaitTicks,
          },
    notifications: state.notifications.map((note) => ({ ...note })),
    nextDroneId: state.nextDroneId,
    nextQueueItemId: state.nextQueueItemId,
    nextNotificationId: state.nextNotificationId,
  };
}

export function createInitialState(seed: number): GameState {
  const world = generateWorld(seed);

  const inventory = emptyInventory();
  // Spec §6 rule 4: the home hex's scavenged material is the starting stock.
  inventory.minerals = { ...world.startingMinerals };

  return {
    tick: 0,
    seed,
    // World generation consumed draws; continue the stream where it left off
    // rather than restarting it, so the sim and the generator cannot correlate.
    rng: world.rng,
    planet: world.planet,
    factory: {
      solarOutput: FACTORY_SOLAR_OUTPUT,
      synthesis: defaultSynthesis(),
      assembly: null,
      assemblyQueue: [],
    },
    drones: startingDrones(),
    mechs: { total: STARTING_MECHS, assigned: 0 },
    city: { grid: emptyGrid() },
    inventory,
    buildQueue: [],
    power: { capacity: FACTORY_SOLAR_OUTPUT, demand: 0, throttleBp: 10000 },
    colony: emptyColony(),
    market: emptyMarket(world.planet),
    production: { goods: zeroed<GoodId>(GOOD_IDS), ticks: 0 },
    starved: null,
    notifications: [],
    nextDroneId: 3,
    nextQueueItemId: 0,
    nextNotificationId: 0,
  };
}
